/**
 * Frontend Logger with LaunchDarkly Observability Integration
 *
 * In development: Outputs to browser console for debugging
 * In production: Sends logs to LaunchDarkly Observability
 *
 * Usage:
 *   import { logError, logWarn, logInfo, logDebug } from '@/lib/logger';
 *   logError("Failed to load data", error);
 *   logWarn("Unexpected state");
 */

import { LDObserve } from '@launchdarkly/observability';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const isDev = import.meta.env.DEV;

/**
 * Internal logging function that routes to console (dev) or LaunchDarkly (prod)
 */
function log(level: LogLevel, message: string, error?: Error | unknown): void {
  const attributes: Record<string, string | undefined> = {};

  // Extract error details if provided
  if (error instanceof Error) {
    attributes.error = error.message;
    attributes.stack = error.stack;
    attributes.errorType = error.name;
  } else if (error !== undefined && error !== null) {
    attributes.error = String(error);
  }

  // Development: output to console for debugging
  if (isDev) {
    const consoleMethod = level === 'debug' ? 'log' : level;
    if (Object.keys(attributes).length > 0) {
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`, attributes);
    } else {
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`);
    }
  }

  // Production: send to LaunchDarkly Observability
  if (!isDev) {
    try {
      LDObserve.recordLog(message, level, attributes);
    } catch {
      // Silently fail if LaunchDarkly not initialized
      // This can happen during early app initialization
    }
  }
}

/**
 * Log a debug-level message (development only)
 */
export function logDebug(message: string): void {
  log('debug', message);
}

/**
 * Log an info-level message
 */
export function logInfo(message: string): void {
  log('info', message);
}

/**
 * Log a warning-level message
 */
export function logWarn(message: string): void {
  log('warn', message);
}

/**
 * Log an error-level message with optional error object
 */
export function logError(message: string, error?: Error | unknown): void {
  log('error', message, error);
}
